/**
 * CompareModal.tsx v3
 * เปรียบเทียบข้อมูล sensor ของทุกโรงเรือนในโปรเจค
 * วางที่: client/src/components/greenhouse/CompareModal.tsx
 *
 * v3 เพิ่ม:
 * - Export Excel / CSV (รวมทุกโรงในไฟล์เดียว แยก column ตามโรง)
 * - Export PNG (จับภาพกราฟ)
 * - รองรับ Mobile (iOS/Android) ด้วย Share sheet
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import {
  X, GitCompare, Clock, RefreshCw, Eye, EyeOff,
  ChevronDown, ChevronUp, BarChart3, Maximize2, Minimize2,
  CalendarDays, AlertCircle, Download, FileSpreadsheet, FileText, Image
} from 'lucide-react';
import { tbApi, TelemetryResponse } from '@/lib/tbApi';
import { projectsApi, Greenhouse } from '@/lib/projectsApi';
import { getSoilKeys, DISPLAY_NAMES, UNITS } from '@/config/dataKeys';
import { formatNumber } from '@/lib/utils';
import { getApiUrl, ENV } from '@/config/env';
import { api } from '@/lib/api';
import { useT } from '@/i18n';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface CompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectKey: string;
  currentGhKey: string;
}

type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';
type SensorType = 'air' | 'soil';
type SoilParam = 'moisture' | 'temp' | 'ec' | 'ph' | 'n' | 'p' | 'k';
type AirParam = 'air_temp' | 'air_humidity' | 'air_co2' | 'air_light';
type ExportFormat = 'excel' | 'csv' | 'png';

interface GhDataMap {
  [ghKey: string]: TelemetryResponse;
}

interface LatestValueMap {
  [ghKey: string]: number | null;
}

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const GH_COLORS = [
  '#22c55e', '#3b82f6', '#f97316', '#ec4899',
  '#8b5cf6', '#14b8a6', '#eab308', '#ef4444',
  '#06b6d4', '#a855f7',
];

const AIR_PARAMS: AirParam[] = ['air_temp', 'air_humidity', 'air_co2', 'air_light'];
const SOIL_PARAMS: SoilParam[] = ['moisture', 'temp', 'ec', 'ph', 'n', 'p', 'k'];

const SOIL_PARAM_LABELS: Record<SoilParam, string> = {
  moisture: 'ความชื้นดิน', temp: 'อุณหภูมิดิน',
  ec: 'EC', ph: 'pH', n: 'N', p: 'P', k: 'K',
};

const TIME_RANGES: { key: TimeRange; label: string; ms: number }[] = [
  { key: '1h',     label: '1 ชม.',    ms: 60 * 60 * 1000 },
  { key: '6h',     label: '6 ชม.',    ms: 6 * 60 * 60 * 1000 },
  { key: '24h',    label: '24 ชม.',   ms: 24 * 60 * 60 * 1000 },
  { key: '7d',     label: '7 วัน',    ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '30d',    label: '30 วัน',   ms: 30 * 24 * 60 * 60 * 1000 },
  { key: 'custom', label: 'กำหนดเอง', ms: 0 },
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function getInterval(diffHours: number): number {
  if (diffHours <= 1) return 60000;
  if (diffHours <= 6) return 300000;
  if (diffHours <= 24) return 900000;
  if (diffHours <= 168) return 3600000;
  return 86400000;
}

function getSensorKey(
  sensorType: SensorType,
  airParam: AirParam,
  soilParam: SoilParam,
  soilNode: number
): string {
  if (sensorType === 'air') return airParam;
  const soilKeys = getSoilKeys(soilNode);
  return soilKeys[soilParam.toUpperCase() as keyof typeof soilKeys];
}

function transformMultiGhData(
  ghDataMap: GhDataMap,
  ghKeys: string[],
  sensorKey: string
): any[] {
  const tsMap = new Map<number, any>();
  for (const ghKey of ghKeys) {
    const data = ghDataMap[ghKey];
    if (!data) continue;
    const values = data[sensorKey] || [];
    for (const point of values) {
      if (!tsMap.has(point.ts)) tsMap.set(point.ts, { timestamp: point.ts });
      const entry = tsMap.get(point.ts);
      const val = typeof point.value === 'number' ? point.value : parseFloat(String(point.value));
      if (!isNaN(val)) entry[ghKey] = val;
    }
  }
  return Array.from(tsMap.values()).sort((a, b) => a.timestamp - b.timestamp);
}

function getLatestValues(
  ghDataMap: GhDataMap,
  ghKeys: string[],
  sensorKey: string
): LatestValueMap {
  const result: LatestValueMap = {};
  for (const ghKey of ghKeys) {
    const data = ghDataMap[ghKey];
    if (!data) { result[ghKey] = null; continue; }
    const values = [...(data[sensorKey] || [])].sort((a, b) => b.ts - a.ts);
    if (values.length === 0) { result[ghKey] = null; continue; }
    const val = typeof values[0].value === 'number'
      ? values[0].value : parseFloat(String(values[0].value));
    result[ghKey] = isNaN(val) ? null : val;
  }
  return result;
}

// ─────────────────────────────────────────────
// Export Helpers
// ─────────────────────────────────────────────

function saveWebFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

async function saveMobileFile(base64Data: string, filename: string): Promise<void> {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');
  await Filesystem.writeFile({ path: filename, data: base64Data, directory: Directory.Cache });
  const fileUri = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
  await Share.share({ title: filename, url: fileUri.uri, dialogTitle: 'บันทึกหรือแชร์ไฟล์' });
}

// ─────────────────────────────────────────────
// Summary Table
// ─────────────────────────────────────────────

function SummaryTable({
  chartData, selectedGhKeys, ghList, unit, ghColorMap, noDataKeys,
}: {
  chartData: any[];
  selectedGhKeys: string[];
  ghList: Greenhouse[];
  unit: string;
  ghColorMap: Record<string, string>;
  noDataKeys: string[];
}) {
  const ghName = (key: string) => ghList.find(g => g.ghKey === key)?.nameTh || key;
  const stats = selectedGhKeys.map((ghKey) => {
    const values = chartData.map(d => d[ghKey]).filter((v): v is number => typeof v === 'number' && !isNaN(v));
    if (values.length === 0) return { ghKey, latest: null, min: null, max: null, avg: null };
    return {
      ghKey,
      latest: values[values.length - 1],
      min: Math.min(...values),
      max: Math.max(...values),
      avg: values.reduce((a, b) => a + b, 0) / values.length,
    };
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse min-w-[320px]">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800/80">
            {['โรงเรือน', 'ล่าสุด', 'ต่ำสุด', 'สูงสุด', 'เฉลี่ย'].map((h, i) => (
              <th key={h} className={`py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 ${i === 0 ? 'text-left' : 'text-right'}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {stats.map(({ ghKey, latest, min, max, avg }) => (
            <tr key={ghKey} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ghColorMap[ghKey] }} />
                  <span className="text-gray-900 dark:text-gray-100 font-medium text-xs">{ghName(ghKey)}</span>
                  {noDataKeys.includes(ghKey) && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-400 rounded flex items-center gap-0.5">
                      <AlertCircle className="w-2.5 h-2.5" />ไม่มีข้อมูล
                    </span>
                  )}
                </div>
              </td>
              {[latest, min, max, avg].map((val, i) => (
                <td key={i} className="px-3 py-2 text-right tabular-nums text-xs text-gray-700 dark:text-gray-300">
                  {val != null
                    ? <span className={i === 0 ? 'font-bold text-primary' : ''}>{formatNumber(val, 1)} {unit}</span>
                    : <span className="text-gray-300 dark:text-gray-600">—</span>
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export function CompareModal({ isOpen, onClose, projectKey, currentGhKey }: CompareModalProps) {
  const { t } = useT();

  // Greenhouse list
  const [ghList, setGhList] = useState<Greenhouse[]>([]);
  const [loadingGhList, setLoadingGhList] = useState(false);

  // Selection
  const [selectedGhKeys, setSelectedGhKeys] = useState<string[]>([]);
  const [sensorType, setSensorType] = useState<SensorType>('air');
  const [selectedAirParam, setSelectedAirParam] = useState<AirParam>('air_temp');
  const [selectedSoilParam, setSelectedSoilParam] = useState<SoilParam>('moisture');
  const [selectedSoilNode, setSelectedSoilNode] = useState<number>(1);
  const [timeRange, setTimeRange] = useState<TimeRange>('6h');

  // Custom date
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customStartTime, setCustomStartTime] = useState('00:00');
  const [customEndTime, setCustomEndTime] = useState('23:59');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customError, setCustomError] = useState('');

  // Data
  const [ghDataMap, setGhDataMap] = useState<GhDataMap>({});
  const [chartData, setChartData] = useState<any[]>([]);
  const [latestValues, setLatestValues] = useState<LatestValueMap>({});
  const [noDataKeys, setNoDataKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI
  const [showTable, setShowTable] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // Ref สำหรับจับภาพกราฟ
  const chartRef = useRef<HTMLDivElement>(null);

  // ── Pin currentGhKey ไว้บนสุด ──
  const orderedGhList = useMemo(() => {
    const current = ghList.find(g => g.ghKey === currentGhKey);
    const rest = ghList.filter(g => g.ghKey !== currentGhKey);
    return current ? [current, ...rest] : rest;
  }, [ghList, currentGhKey]);

  const ghColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    orderedGhList.forEach((gh, idx) => { map[gh.ghKey] = GH_COLORS[idx % GH_COLORS.length]; });
    return map;
  }, [orderedGhList]);

  const sensorKey = useMemo(() =>
    getSensorKey(sensorType, selectedAirParam, selectedSoilParam, selectedSoilNode),
    [sensorType, selectedAirParam, selectedSoilParam, selectedSoilNode]
  );

  const currentUnit = sensorType === 'air' ? (UNITS[selectedAirParam] || '') : (UNITS[selectedSoilParam] || '');
  const currentLabel = sensorType === 'air'
    ? (DISPLAY_NAMES[selectedAirParam] || selectedAirParam)
    : SOIL_PARAM_LABELS[selectedSoilParam];

  // ── Load greenhouse list ──
  useEffect(() => {
    if (!isOpen) return;
    setLoadingGhList(true);
    projectsApi.getGreenhouses(projectKey)
      .then(({ greenhouses }) => {
        const ready = greenhouses.filter(g => g.status === 'ready' && g.hasDevice);
        setGhList(ready);
        setSelectedGhKeys(ready.map(g => g.ghKey));
      })
      .catch(() => setGhList([]))
      .finally(() => setLoadingGhList(false));
  }, [isOpen, projectKey]);

  // ── Get time range ──
  const getTimeRangeTs = useCallback((): { startTs: number; endTs: number } | null => {
    if (timeRange === 'custom') {
      if (!customStart || !customEnd) return null;
      const startTs = new Date(`${customStart}T${customStartTime}`).getTime();
      const endTs = new Date(`${customEnd}T${customEndTime}`).getTime();
      if (startTs >= endTs) return null;
      return { startTs, endTs };
    }
    const range = TIME_RANGES.find(r => r.key === timeRange)!;
    return { startTs: Date.now() - range.ms, endTs: Date.now() };
  }, [timeRange, customStart, customEnd, customStartTime, customEndTime]);

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    if (selectedGhKeys.length === 0) return;
    const ts = getTimeRangeTs();
    if (!ts) return;
    setIsLoading(true);
    setError(null);
    const diffHours = (ts.endTs - ts.startTs) / (1000 * 60 * 60);
    const interval = getInterval(diffHours);
    try {
      const results: GhDataMap = {};
      await Promise.all(
        selectedGhKeys.map(async (ghKey) => {
          try {
            results[ghKey] = await tbApi.getTimeseries(
              projectKey, ghKey, [sensorKey], ts.startTs, ts.endTs,
              { interval, agg: 'AVG', limit: 1000 }
            );
          } catch { results[ghKey] = {}; }
        })
      );
      setGhDataMap(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setIsLoading(false);
    }
  }, [selectedGhKeys, sensorKey, getTimeRangeTs, projectKey]);

  // ── Transform ──
  useEffect(() => {
    const data = transformMultiGhData(ghDataMap, selectedGhKeys, sensorKey);
    const latest = getLatestValues(ghDataMap, selectedGhKeys, sensorKey);
    const noData = selectedGhKeys.filter(k => {
      const d = ghDataMap[k];
      return !d || (d[sensorKey] || []).length === 0;
    });
    setChartData(data);
    setLatestValues(latest);
    setNoDataKeys(noData);
  }, [ghDataMap, selectedGhKeys, sensorKey]);

  // ── Auto fetch ──
  useEffect(() => {
    if (isOpen && selectedGhKeys.length > 0 && timeRange !== 'custom') fetchData();
  }, [isOpen, timeRange, sensorType, selectedAirParam, selectedSoilParam, selectedSoilNode]);

  // ── Custom date apply ──
  const applyCustomRange = () => {
    setCustomError('');
    if (!customStart || !customEnd) { setCustomError('กรุณาเลือกวันเริ่มต้นและสิ้นสุด'); return; }
    const s = new Date(`${customStart}T${customStartTime}`);
    const e = new Date(`${customEnd}T${customEndTime}`);
    if (s >= e) { setCustomError('วันเริ่มต้นต้องก่อนวันสิ้นสุด'); return; }
    const diffDays = (e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays > 90) { setCustomError('เลือกได้สูงสุด 90 วัน'); return; }
    setShowCustomPicker(false);
    fetchData();
  };

  // ── Export ──
  const handleExport = async (format: ExportFormat) => {
    setShowExportMenu(false);
    setExportError('');

    if (format === 'png') {
      // Export PNG ด้วย html2canvas
      if (!chartRef.current) return;
      setIsExporting(true);
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(chartRef.current as HTMLElement, {
          background: '#ffffff',
          scale: 2,
          useCORS: true,
        } as any);
        const filename = `compare-${projectKey}-${currentLabel}-${new Date().toISOString().split('T')[0]}.png`;

        if (ENV.IS_CAPACITOR) {
          const base64 = canvas.toDataURL('image/png').split(',')[1];
          await saveMobileFile(base64, filename);
        } else {
          canvas.toBlob((blob) => {
            if (blob) saveWebFile(blob, filename);
          }, 'image/png');
        }
      } catch (err) {
        setExportError('ไม่สามารถจับภาพกราฟได้');
      } finally {
        setIsExporting(false);
      }
      return;
    }

    // Export Excel / CSV — ส่งข้อมูลทุกโรงรวมกัน
    const ts = getTimeRangeTs();
    if (!ts) { setExportError('กรุณาเลือกช่วงเวลาก่อน'); return; }

    if (selectedGhKeys.length === 0) { setExportError('กรุณาเลือกโรงเรือนอย่างน้อย 1 โรง'); return; }

    setIsExporting(true);
    try {
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      const dateStr = `${new Date(ts.startTs).toISOString().split('T')[0]}_to_${new Date(ts.endTs).toISOString().split('T')[0]}`;
      const filename = `compare-${projectKey}-${currentLabel}-${dateStr}.${ext}`;
      const endpoint = format === 'excel' ? '/api/export/telemetry/excel' : '/api/export/telemetry/csv';
      const url = getApiUrl(endpoint);

      // สร้าง payload รวมทุกโรง (keys เดิม แต่ส่งทีละโรงแล้ว merge หรือส่ง ghKeys array)
      // ใช้วิธีส่งทีละโรงแล้ว merge blob บน client
      const blobs: Blob[] = [];
      const csrf = await api.getCsrfToken();

      for (const ghKey of selectedGhKeys) {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
          credentials: 'include',
          body: JSON.stringify({
            projectKey,
            ghKey,
            keys: [sensorKey],
            startTs: ts.startTs,
            endTs: ts.endTs,
          }),
        });
        if (response.ok) {
          blobs.push(await response.blob());
        }
      }

      if (blobs.length === 0) {
        setExportError('ไม่มีข้อมูลที่จะ Export');
        return;
      }

      // ถ้ามีแค่โรงเดียว download ตรงๆ
      // ถ้ามีหลายโรง merge เป็น zip หรือ download ทีละไฟล์
      if (blobs.length === 1) {
        const singleFilename = `compare-${projectKey}-${selectedGhKeys[0]}-${currentLabel}-${dateStr}.${ext}`;
        if (ENV.IS_CAPACITOR) {
          const reader = new FileReader();
          reader.onload = async () => {
            const base64 = (reader.result as string).split(',')[1];
            await saveMobileFile(base64, singleFilename);
          };
          reader.readAsDataURL(blobs[0]);
        } else {
          saveWebFile(blobs[0], singleFilename);
        }
      } else {
        // Download ทีละไฟล์ (แต่ละโรงเรือน)
        for (let i = 0; i < blobs.length; i++) {
          const ghKey = selectedGhKeys[i];
          const ghName = orderedGhList.find(g => g.ghKey === ghKey)?.nameTh || ghKey;
          const perFilename = `compare-${ghName}-${currentLabel}-${dateStr}.${ext}`;
          if (ENV.IS_CAPACITOR) {
            const reader = new FileReader();
            reader.onload = async () => {
              const base64 = (reader.result as string).split(',')[1];
              await saveMobileFile(base64, perFilename);
            };
            reader.readAsDataURL(blobs[i]);
          } else {
            // delay เล็กน้อยระหว่างแต่ละไฟล์
            await new Promise(resolve => setTimeout(resolve, i * 300));
            saveWebFile(blobs[i], perFilename);
          }
        }
      }
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export ไม่สำเร็จ');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Helpers ──
  const toggleGh = (ghKey: string) =>
    setSelectedGhKeys(prev =>
      prev.includes(ghKey) ? prev.filter(k => k !== ghKey) : [...prev, ghKey]
    );
  const selectAllGh = () => setSelectedGhKeys(orderedGhList.map(g => g.ghKey));
  const deselectAllGh = () => setSelectedGhKeys([]);

  const formatXAxis = (ts: number) => {
    const date = new Date(ts);
    const tsRange = getTimeRangeTs();
    if (!tsRange) return '';
    const diffHours = (tsRange.endTs - tsRange.startTs) / (1000 * 60 * 60);
    return diffHours <= 24
      ? date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('th-TH', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 border border-gray-200 dark:border-gray-700 max-w-[200px]">
        <p className="text-xs text-gray-500 mb-1.5 font-medium">{new Date(label).toLocaleString('th-TH')}</p>
        <div className="space-y-1">
          {payload.map((entry: any) => {
            const gh = orderedGhList.find(g => g.ghKey === entry.dataKey);
            return (
              <div key={entry.dataKey} className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-600 dark:text-gray-300 truncate flex-1">{gh?.nameTh || entry.dataKey}:</span>
                <span className="font-bold tabular-nums">{formatNumber(entry.value, 1)} {currentUnit}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const modalClass = isFullscreen
    ? 'fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-900 overflow-hidden'
    : 'relative w-full max-w-5xl max-h-[92vh] mx-3 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden';

  return (
    <div className={`fixed inset-0 z-50 ${isFullscreen ? '' : 'flex items-center justify-center p-2'}`}>
      {!isFullscreen && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      )}

      <div className={modalClass}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary/5 to-transparent flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <GitCompare className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-tight">เปรียบเทียบโรงเรือน</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{orderedGhList.length} โรงเรือนในโปรเจคนี้</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setIsFullscreen(p => !p)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
              {isFullscreen ? <Minimize2 className="w-4 h-4 text-gray-500" /> : <Maximize2 className="w-4 h-4 text-gray-500" />}
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* ── Controls ── */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 flex-shrink-0 space-y-2.5">

          {/* Sensor type + Params */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 flex-shrink-0">
              {(['air', 'soil'] as SensorType[]).map(type => (
                <button key={type} onClick={() => setSensorType(type)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    sensorType === type ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                  }`}>
                  {type === 'air' ? '🌡 อากาศ' : '🌱 ดิน'}
                </button>
              ))}
            </div>
            {sensorType === 'air'
              ? AIR_PARAMS.map(param => (
                  <button key={param} onClick={() => setSelectedAirParam(param)}
                    className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                      selectedAirParam === param
                        ? 'bg-primary/10 border-primary text-primary font-semibold'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                    }`}>
                    {DISPLAY_NAMES[param]}
                  </button>
                ))
              : <>
                  {SOIL_PARAMS.map(param => (
                    <button key={param} onClick={() => setSelectedSoilParam(param)}
                      className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                        selectedSoilParam === param
                          ? 'bg-primary/10 border-primary text-primary font-semibold'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}>
                      {SOIL_PARAM_LABELS[param]}
                    </button>
                  ))}
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-400">Node:</span>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setSelectedSoilNode(n)}
                        className={`w-6 h-6 text-xs rounded-md transition-colors ${
                          selectedSoilNode === n ? 'bg-primary text-white font-semibold' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-600'
                        }`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </>
            }
          </div>

          {/* Time range */}
          <div className="flex flex-wrap items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <div className="flex gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              {TIME_RANGES.map(r => (
                <button key={r.key}
                  onClick={() => {
                    if (r.key === 'custom') { setShowCustomPicker(true); setTimeRange('custom'); }
                    else { setTimeRange(r.key as TimeRange); setShowCustomPicker(false); }
                  }}
                  className={`px-2 py-1 text-xs rounded-md transition-colors whitespace-nowrap ${
                    timeRange === r.key
                      ? 'bg-white dark:bg-gray-600 text-primary font-semibold shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                  {r.label}
                </button>
              ))}
            </div>
            {timeRange === 'custom' && customStart && customEnd && !showCustomPicker && (
              <button onClick={() => setShowCustomPicker(true)}
                className="flex items-center gap-1 text-xs text-primary bg-primary/5 px-2 py-1 rounded-lg">
                <CalendarDays className="w-3 h-3" />
                {new Date(`${customStart}T${customStartTime}`).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}
                {' – '}
                {new Date(`${customEnd}T${customEndTime}`).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}
              </button>
            )}
            <div className="flex-1" />
            <button onClick={fetchData} disabled={isLoading}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Custom date picker */}
          {showCustomPicker && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">📅 กำหนดช่วงเวลา (สูงสุด 90 วัน)</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'เริ่มต้น', date: customStart, setDate: setCustomStart, time: customStartTime, setTime: setCustomStartTime },
                  { label: 'สิ้นสุด', date: customEnd, setDate: setCustomEnd, time: customEndTime, setTime: setCustomEndTime },
                ].map(({ label, date, setDate, time, setTime }) => (
                  <div key={label} className="space-y-1">
                    <p className="text-xs text-gray-500">{label}</p>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/50" />
                    <input type="time" value={time} onChange={e => setTime(e.target.value)}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/50" />
                  </div>
                ))}
              </div>
              {customError && <p className="text-xs text-red-500">{customError}</p>}
              <div className="flex gap-2">
                <button onClick={applyCustomRange}
                  className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-primary/90 transition-colors">
                  ยืนยัน
                </button>
                <button onClick={() => { setShowCustomPicker(false); if (!customStart) setTimeRange('6h'); }}
                  className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs hover:bg-gray-300 transition-colors">
                  ยกเลิก
                </button>
              </div>
            </div>
          )}

          {/* Greenhouse toggles */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-xs text-gray-500 font-medium flex-shrink-0">โรงเรือน:</span>
            <button onClick={selectedGhKeys.length === orderedGhList.length ? deselectAllGh : selectAllGh}
              className="px-2 py-0.5 text-xs rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:border-primary hover:text-primary transition-colors flex-shrink-0">
              {selectedGhKeys.length === orderedGhList.length ? 'ยกเลิกทั้งหมด' : 'ทั้งหมด'}
            </button>
            {loadingGhList ? (
              <span className="text-xs text-gray-400">กำลังโหลด...</span>
            ) : (
              orderedGhList.map((gh) => {
                const isSelected = selectedGhKeys.includes(gh.ghKey);
                const color = ghColorMap[gh.ghKey];
                const isCurrent = gh.ghKey === currentGhKey;
                const latest = latestValues[gh.ghKey];
                const hasNoData = noDataKeys.includes(gh.ghKey) && Object.keys(ghDataMap).length > 0;
                return (
                  <button key={gh.ghKey} onClick={() => toggleGh(gh.ghKey)}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg border-2 transition-all ${
                      isSelected ? 'bg-white dark:bg-gray-800 shadow-sm' : 'bg-gray-100 dark:bg-gray-700 opacity-50'
                    } ${isCurrent ? 'ring-1 ring-offset-1 ring-primary/50' : ''}`}
                    style={isSelected ? { borderColor: color } : { borderColor: 'transparent' }}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color, opacity: isSelected ? 1 : 0.4 }} />
                    <span className={isSelected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500'}>
                      {gh.nameTh}
                      {isCurrent && <span className="ml-0.5 text-primary text-[9px]">●</span>}
                    </span>
                    {isSelected && latest != null && (
                      <span className="text-[10px] font-bold tabular-nums" style={{ color }}>
                        {formatNumber(latest, 1)}{currentUnit}
                      </span>
                    )}
                    {hasNoData && <AlertCircle className="w-3 h-3 text-amber-400" />}
                    {isSelected ? <Eye className="w-3 h-3 text-gray-300" /> : <EyeOff className="w-3 h-3 text-gray-400" />}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-3 text-xs text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {exportError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-3 text-xs text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {exportError}
            </div>
          )}

          {noDataKeys.length > 0 && Object.keys(ghDataMap).length > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                <span className="font-semibold">ไม่มีข้อมูล: </span>
                {noDataKeys.map(k => orderedGhList.find(g => g.ghKey === k)?.nameTh || k).join(', ')}
                {' '}ในช่วงเวลานี้
              </span>
            </div>
          )}

          {selectedGhKeys.length === 0 && !isLoading && (
            <div className="text-center py-12 text-gray-400">
              <GitCompare className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">เลือกโรงเรือนอย่างน้อย 1 โรง</p>
            </div>
          )}

          {/* Chart */}
          {selectedGhKeys.length > 0 && (
            <div ref={chartRef} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                    {currentLabel}
                    {currentUnit && <span className="text-gray-400 font-normal ml-1 text-xs">({currentUnit})</span>}
                  </span>
                </div>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {timeRange === 'custom' && customStart
                    ? `${customStart} – ${customEnd}`
                    : TIME_RANGES.find(r => r.key === timeRange)?.label}
                </span>
              </div>
              <div className="p-3">
                {isLoading ? (
                  <div className="h-56 flex items-center justify-center text-gray-400 text-sm gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" /> กำลังโหลด...
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="h-56 flex items-center justify-center text-gray-400 text-sm">
                    ไม่มีข้อมูลในช่วงเวลานี้
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={isFullscreen ? 400 : 260}>
                    <LineChart data={chartData} margin={{ top: 5, right: 8, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
                      <XAxis dataKey="timestamp" tickFormatter={formatXAxis} tick={{ fontSize: 10 }} stroke="#9ca3af" />
                      <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" width={42}
                        label={{ value: currentUnit, angle: -90, position: 'insideLeft', fontSize: 10, fill: '#9ca3af' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        formatter={(value) => {
                          const gh = orderedGhList.find(g => g.ghKey === value);
                          return <span style={{ fontSize: 11 }}>{gh?.nameTh || value}</span>;
                        }}
                        iconType="line" wrapperStyle={{ paddingTop: 8 }}
                      />
                      {selectedGhKeys.map((ghKey) => (
                        <Line key={ghKey} type="monotone" dataKey={ghKey}
                          stroke={ghColorMap[ghKey]}
                          strokeWidth={ghKey === currentGhKey ? 2.5 : 1.5}
                          dot={false} connectNulls activeDot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}

          {/* Summary Table */}
          {selectedGhKeys.length > 0 && chartData.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button onClick={() => setShowTable(p => !p)}
                className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">📋 สรุปค่าสถิติ</span>
                {showTable ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showTable && (
                <SummaryTable
                  chartData={chartData}
                  selectedGhKeys={selectedGhKeys}
                  ghList={orderedGhList}
                  unit={currentUnit}
                  ghColorMap={ghColorMap}
                  noDataKeys={noDataKeys}
                />
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between flex-shrink-0 bg-gray-50/50 dark:bg-gray-800/30">
          <span className="text-xs text-gray-400">
            {selectedGhKeys.length > 0 ? `เปรียบเทียบ ${selectedGhKeys.length} โรงเรือน` : 'ยังไม่ได้เลือกโรงเรือน'}
            {noDataKeys.length > 0 && ` • ไม่มีข้อมูล ${noDataKeys.length} โรง`}
          </span>

          <div className="flex items-center gap-2">
            {/* Export Button */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(p => !p)}
                disabled={isExporting || chartData.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                {isExporting ? 'กำลัง Export...' : 'Export'}
              </button>

              {/* Export dropdown */}
              {showExportMenu && (
                <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden min-w-[160px] z-10">
                  <button onClick={() => handleExport('excel')}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300">
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    Excel (.xlsx)
                  </button>
                  <button onClick={() => handleExport('csv')}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700">
                    <FileText className="w-4 h-4 text-blue-600" />
                    CSV
                  </button>
                  <button onClick={() => handleExport('png')}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700">
                    <Image className="w-4 h-4 text-purple-600" />
                    รูปภาพ (.png)
                  </button>
                </div>
              )}
            </div>

            <button onClick={onClose}
              className="px-4 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors font-medium">
              ปิด
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}